import {
  Box,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useRadioGroup,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { T_KEY } from "@/src/constants/translationsKey";

import type { ITabs, TabPanelType } from "../../../../../utils/types";
import RadioCard from "./RadioCard";

import styles from "./GuideContentBody.module.scss";

// 2:1, 4:1, 5:1이 아니면 3:1을 반환한다. (기본값)
export const extractValueFromSrc = (src: string) => {
  const tempo = src?.split("/")?.pop()?.split("-")?.pop()?.split(".")?.shift();

  if (!tempo) {
    return null;
  }
  if (["2_1", "4_1", "5_1"].includes(tempo)) {
    return tempo.replace("_", ":");
  }
  if (["driver", "iron"].includes(tempo)) {
    return "3:1";
  }
  return null;
};

export const autoPlayVideo = (
  video: HTMLVideoElement | null,
  srcValue: string,
  initialVideoPlay: React.MutableRefObject<boolean>
) => {
  if ((video && srcValue !== "3:1") || (video && !initialVideoPlay.current)) {
    video.load();
    video.play();
  }
};

interface ContentsProps {
  selectedValue: string;
  tabIndex: number;
  videoSrc: string[];
  panel: TabPanelType;
}

function generatePanelContents({
  selectedValue,
  tabIndex,
  videoSrc,
  panel,
}: ContentsProps) {
  if (!panel?.contents || panel.contents.length === 0) {
    return null;
  }

  return panel.contents.map((content, contentIndex) => {
    // TODO: tabPanel에 다른 컨텐츠가 추가되면 수정해야 함
    // 2:1 -> 2_1로 변경해야 src를 추출할 수 있음
    const replacedSrc = extractValueFromSrc(content.src);
    if (
      !content.src ||
      replacedSrc !== selectedValue ||
      !content.src.includes(videoSrc[`${tabIndex}`])
    ) {
      return null;
    }

    // 3:1을 제외한 비디오 자동 재생
    const videoRef = useRef<HTMLVideoElement>(null);
    // video가 play되는 것을 인식해서 initialVideoPlay를 false로 변경한다. (3:1 제외)
    const initialVideoPlay = useRef(true);
    useEffect(() => {
      autoPlayVideo(videoRef.current, replacedSrc, initialVideoPlay);
    }, [content?.src, replacedSrc]);

    const onPlay = () => {
      initialVideoPlay.current = false;
    };

    return (
      <Box
        key={contentIndex}
        {...content?.styles?.container}
        className={styles.videoContainer}
      >
        <video
          ref={videoRef}
          src={content?.src}
          controls
          controlsList="nodownload"
          muted
          playsInline
          style={{
            width: "100%",
            height: "auto",
            aspectRatio: "4 / 5",
            ...content?.style,
          }}
          {...content?.attributes}
          onPlay={onPlay}
        >
          <track default kind="captions" srcLang="en" src={content?.src} />
        </video>
      </Box>
    );
  });
}

interface TabsContentProps {
  field: ITabs;
}

export default function TabsContent({ field }: TabsContentProps) {
  const { t } = useTranslation(T_KEY);
  const [selectedValue, setSelectedValue] = useState("3:1");

  const { getRootProps, getRadioProps } = useRadioGroup({
    name: "rythm-tempo",
    defaultValue: "3:1",
    onChange: setSelectedValue,
  });

  const group = getRootProps();

  const [tabIndex, setTabIndex] = useState(0);
  const videoSrc = ["front-driver", "side-driver", "front-iron", "side-iron"];

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };

  return (
    <Tabs colorScheme="gray.200" onChange={handleTabChange}>
      <TabList {...field.styles?.tabList}>
        {field.tabTitles?.map((tabTitle, tabIdx) => (
          <Tab key={tabIdx} fontSize="lg">
            {`${t(tabTitle[0])} - ${t(tabTitle[1])}`}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {field.panels?.map((panel, panelIndex) => (
          <TabPanel
            key={panelIndex}
            style={{
              padding: "0",
            }}
          >
            <Box m="0 auto">
              {generatePanelContents({
                selectedValue,
                tabIndex,
                videoSrc,
                panel,
              })}
            </Box>

            <HStack {...group} gap="16px" flex="wrap">
              {panel.options.map((value, optionIndex) => {
                const radio = getRadioProps({ value });
                return (
                  <RadioCard key={optionIndex} {...radio}>
                    {value}
                  </RadioCard>
                );
              })}
            </HStack>
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
}
